import {
	addUnit,
	formatDate,
	getUnit,
	isSame,
	localOrUtcTimestampToLocalDate,
	subtractUnit,
} from "@sembark-travel/datetime-utils"
import { moneyParseByDecimal } from "@sembark-travel/money"
import { isNumeric } from "@sembark-travel/number-utils"
import {
	Alert,
	Box,
	Button,
	CheckboxInput,
	Dropdown,
	Icons,
	Inline,
	joinAttributes,
	Money,
	SelectInput,
	Spinner,
	Stack,
	Stars,
	Text,
	Time,
	Tooltip,
	useBreakpoints,
} from "@sembark-travel/ui-base"
import { DatePickerField, SelectField } from "@sembark-travel/ui-form"
import {
	areAdvancedFiltersAppliedDefault,
	Search,
	useSearch,
	useSearchContext,
} from "@sembark-travel/ui-list"
import { ButtonLink, useLocationQuery } from "@sembark-travel/ui-router"
import { useXHR, XHRInstance } from "@sembark-travel/xhr"
import pluralize from "pluralize"
import { useEffect, useMemo } from "react"
import useSWR from "swr"
import { Required } from "utility-types"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { SelectHotelBookingStages } from "../HotelBookingStages"
import { SelectHotelGroup } from "../HotelGroups"
import { SelectHotels } from "../Hotels"
import { generatePath } from "../router-utils"
import { SelectTripDestination } from "../TripDestinations"
import { useTripQuoteBookingsDiffCount } from "../TripQuoteBookingsDiff"
import { SelectTripSources } from "../TripSources"
import { SelectUsers } from "../Users"
import { SelectHotelBookingStageTags } from "./BookingStatus"
import { HotelBookingItemInDialog } from "./Item"
import { SelectHotelBookingPaymentStatuses } from "./List"
import {
	filtersToLocationQuery,
	IBooking,
	locationQueryToFilters,
	TBookingSlot,
} from "./store"
import {
	arrangeHotelBookingsInCalendar,
	BOOKING_STAGE_META,
	filtersToRequestParams,
	getPaxDisplay,
	IFilters,
	IFiltersInLocation,
	TransitionWindowOptions,
	useCalendarUIPreferences,
} from "./utils"

type TDayStat = {
	checkins: number
	checkouts: number
	total: number
	booked: number
	pending: number
}

type THotelOperationalBookingCalendarFilters = Required<IFilters, "date">

// Fetch Hotel Bookings
function XHR(xhr: XHRInstance) {
	return {
		async getHotelBookings(
			params: Record<string, unknown>
		): Promise<Array<IBooking>> {
			return xhr
				.get("/hotel-bookings", {
					params: {
						...params,
						pagination: "all",
					},
				})
				.then((resp) => resp.data.data)
		},
	}
}

// To get the Dates of interval window in the respect of current query date
function getDaysForIntervalWindow(
	intervalWindow: number,
	base: Date = new Date(),
	alignDateToStart = false
) {
	const min = alignDateToStart
		? 0
		: intervalWindow % 2 === 0
			? intervalWindow / 2 - 1
			: Math.floor(intervalWindow / 2)
	const max = alignDateToStart ? intervalWindow : Math.floor(intervalWindow / 2)
	const interval: Date[] = []
	for (let i = min; i >= 0; i--) {
		interval.push(subtractUnit(base, i, "days"))
	}
	for (let i = 1; i <= max; i++) {
		interval.push(addUnit(base, i, "days"))
	}
	return interval.slice(0, intervalWindow)
}

// ----- Per day metrics helper-------
const DATE_KEY_FORMAT = "YYYY-MM-DD"

const getDateKey = (date: Date | string) => formatDate(date, DATE_KEY_FORMAT)

const createEmptyStat = (): TDayStat => ({
	checkins: 0,
	checkouts: 0,
	total: 0,
	booked: 0,
	pending: 0,
})

function getOrCreateStat(map: Map<string, TDayStat>, key: string): TDayStat {
	let stat = map.get(key)

	if (!stat) {
		stat = createEmptyStat()
		map.set(key, stat)
	}

	return stat
}

//  Main
export function HotelOperationalCalendar() {
	const [queryFilters, setQueryFilters] = useLocationQuery<
		THotelOperationalBookingCalendarFilters,
		IFiltersInLocation
	>({
		toQuery: filtersToLocationQuery,
		fromQuery: locationQueryToFilters as never,
	})

	const [params, setParams] =
		useSearch<THotelOperationalBookingCalendarFilters>({
			q: "",
			...queryFilters,
			date: queryFilters.date || new Date(),
		})

	// To sync query state and url query
	useEffect(() => {
		setQueryFilters(params)
	}, [params, setQueryFilters])

	const {
		calendarUIPreferences,
		toggleCompactFit,
		changeTransitionWindowSize,
		toggleResvTeamVisibility,
	} = useCalendarUIPreferences()

	const { hasPermission } = useCheckPermissions()
	const canViewRevTeam = hasPermission(PERMISSIONS.MANAGE_TRIP_OWNERS)
	const tripQuoteBookingsDiffCount = useTripQuoteBookingsDiffCount({
		hotels_changed: true,
	})

	return (
		<Search<THotelOperationalBookingCalendarFilters>
			initialParams={params}
			title="Hotel Bookings"
			onSearch={(params) => setParams(params)}
			areAdvancedFiltersApplied={(filters) => {
				const { date, ...otherFilters } = filters
				return areAdvancedFiltersAppliedDefault(otherFilters)
			}}
			resetParams={(params) => ({
				q: "",
				date: params.date,
			})}
			actions={({ setSearchParamValue, searchParams: params }) => (
				<Box>
					<Inline gap="1">
						<NavigationButton
							days={-calendarUIPreferences.transitionWindowSize}
							onClick={(days) => {
								setSearchParamValue("date", addUnit(params.date, days, "day"))
							}}
						/>
						<NavigationButton
							days={calendarUIPreferences.transitionWindowSize}
							onClick={(days) => {
								setSearchParamValue("date", addUnit(params.date, days, "day"))
							}}
						/>
						<ButtonLink to={generatePath("/hotel-bookings")} level="primary">
							<Icons.ViewList /> List
						</ButtonLink>
						{tripQuoteBookingsDiffCount ? (
							<ButtonLink
								to={generatePath("/trip-quote-bookings-diff")}
								status="warning"
								title="Trip Quote & Hotel Bookings Diff"
							>
								<Inline alignItems="center" as="span" gap="1">
									<Box as="span">
										<Icons.Attention />
									</Box>
									<Text as="span">{tripQuoteBookingsDiffCount}</Text>
								</Inline>
							</ButtonLink>
						) : null}
						<Dropdown alignRight="sm" display="inlineBlock">
							<Dropdown.ToggleButton>
								<Icons.DotsVertical />
							</Dropdown.ToggleButton>
							<Dropdown.Menu>
								<Dropdown.MenuSectionHeader>
									<Icons.Cog /> UI Preferences
								</Dropdown.MenuSectionHeader>
								<li>
									<Box
										as="label"
										cursor="pointer"
										paddingX="3"
										paddingY="2"
										display="block"
										fontWeight="normal"
									>
										<Box display="flex" alignItems="center">
											<CheckboxInput
												id="toggle_compact_fit"
												checked={calendarUIPreferences.hasCompactFit}
												onChange={() => {
													toggleCompactFit()
												}}
											/>
											<Box paddingLeft="2">
												<Box>Use Compact Fit</Box>
												<Box fontSize="sm" color="muted">
													Move items upwards when space is available.
												</Box>
											</Box>
										</Box>
									</Box>
								</li>
								{canViewRevTeam ? (
									<li>
										<Box
											as="label"
											cursor="pointer"
											paddingX="3"
											paddingY="2"
											display="block"
											fontWeight="normal"
										>
											<Box display="flex" alignItems="center">
												<CheckboxInput
													id="toggle_resv_team_visibility"
													checked={calendarUIPreferences.showResvTeam}
													onChange={() => {
														toggleResvTeamVisibility()
													}}
												/>
												<Box paddingLeft="2">
													<Box>Show/Hide Reservations Team</Box>
												</Box>
											</Box>
										</Box>
									</li>
								) : null}
								<li>
									<Box
										as="label"
										cursor="pointer"
										paddingX="3"
										paddingY="2"
										display="block"
										fontWeight="normal"
									>
										<Box display="flex" alignItems="center">
											<SelectInput
												id="change_transition_window_size"
												value={calendarUIPreferences.transitionWindowSize}
												onChange={(e) => {
													changeTransitionWindowSize(e.currentTarget.value)
												}}
											>
												{TransitionWindowOptions.map((option) => (
													<option key={option} value={option}>
														{option} {pluralize("day", option)}
													</option>
												))}
											</SelectInput>
											<Box paddingLeft="2">
												<Box>Transition Window Size</Box>
												<Box fontSize="sm" color="muted">
													Days to skip on next/previous
												</Box>
											</Box>
										</Box>
									</Box>
								</li>
							</Dropdown.Menu>
						</Dropdown>
					</Inline>
				</Box>
			)}
			Filters={Filters}
		>
			<OperationalCalendar
				params={params}
				date={params.date as Date}
				{...calendarUIPreferences}
			/>
		</Search>
	)
}

// OperationalCalendar is the component that renders the calendar view of hotel operational bookings.
function OperationalCalendar({
	params,
	date,
	hasCompactFit,
	showResvTeam,
	alignDateToStart,
}: {
	params: THotelOperationalBookingCalendarFilters
	date: Date
	hasCompactFit: boolean
	showResvTeam: boolean
	alignDateToStart?: boolean
}) {
	const xhr = useXHR()
	const searchContext = useSearchContext()
	const breakpoints = useBreakpoints()
	const { xs, sm, md, lg } = breakpoints
	const { hasPermission } = useCheckPermissions()
	let additionalInterval = 2

	if (searchContext) {
		if (!searchContext.areFilterVisible && !xs && !sm) {
			additionalInterval += 3
		}
	}
	const intervalWindow =
		(xs ? 2 : sm ? 4 : md ? 4 : lg ? 6 : 7) + additionalInterval

	const interval = useMemo(() => {
		return getDaysForIntervalWindow(intervalWindow, date, alignDateToStart)
	}, [date, intervalWindow, alignDateToStart])

	const filters = useMemo(() => {
		const baseParams = filtersToRequestParams({
			...params,
			overall_checkin_before: interval[interval.length - 1],
			overall_checkout_after: interval[0],
		})
		const hasUserSelectedStages =
			params.booking_stages && params.booking_stages.length > 0
		return {
			...baseParams,
			booking_stages: hasUserSelectedStages
				? (params.booking_stages || []).map((s) => String(s.id))
				: ["-dropped", "-changed", "-trip_dropped"],
		}
	}, [params, interval])

	const {
		data,
		error,
		isValidating,
		mutate: revalidate,
	} = useSWR(
		`hotel_bookings_${JSON.stringify(filters)}`,
		() => XHR(xhr).getHotelBookings(filters),
		{
			refreshInterval: 60000,
		}
	)

	const isFetching = !data

	const now = useMemo(() => new Date(), [])

	// this is because data can be undefined when it's being fetched for the first time,
	// and we don't want to break the calendar rendering in that case.
	// Once the data is fetched, it will be stored in hotel_bookings and used for rendering.
	const hotel_bookings: IBooking[] = useMemo(() => {
		return data || []
	}, [data])

	const schedulesPerBookingPerSlot = useMemo(() => {
		return arrangeHotelBookingsInCalendar(
			hotel_bookings,
			interval,
			hasCompactFit
		)
	}, [hotel_bookings, interval, hasCompactFit])

	// Created map to store per day metrics
	const statsMap = useMemo(() => {
		const map = new Map<string, TDayStat>()

		hotel_bookings.forEach((booking) => {
			const checkinKey = getDateKey(
				localOrUtcTimestampToLocalDate(booking.checkin_local, booking.checkin)
			)

			const checkoutKey = getDateKey(
				localOrUtcTimestampToLocalDate(booking.checkout_local, booking.checkout)
			)

			// Check-in
			getOrCreateStat(map, checkinKey).checkins++

			// Check-out
			getOrCreateStat(map, checkoutKey).checkouts++

			// Room Nights
			booking.details?.forEach((detail) => {
				const key = getDateKey(
					localOrUtcTimestampToLocalDate(detail.date_local, detail.date)
				)
				const stat = getOrCreateStat(map, key)

				const rooms = detail.no_of_rooms || 1

				stat.total += rooms

				if (booking.booked) {
					stat.booked += rooms
				} else {
					stat.pending += rooms
				}
			})
		})

		return map
	}, [hotel_bookings])

	// To show day metrics of check-ins , check-outs , booked and pending of room nights
	const dailyStats = useMemo(() => {
		return interval.map((day) => {
			const key = getDateKey(day)

			const stat = statsMap.get(key)

			return {
				day,
				checkins: stat?.checkins || 0,
				checkouts: stat?.checkouts || 0,
				total: stat?.total || 0,
				booked: stat?.booked || 0,
				pending: stat?.pending || 0,
			}
		})
	}, [interval, statsMap])

	const showResvationsTeam =
		showResvTeam && hasPermission(PERMISSIONS.MANAGE_TRIP_OWNERS)

	if (error && !data) {
		return (
			<Alert status="error">
				<Stack gap="2">
					<Text>{error.message}</Text>
					<Box>
						<Button
							onClick={() => revalidate()}
							disabled={isValidating}
							size="sm"
							level="primary"
						>
							<Icons.Refresh spin={isValidating} /> Try Again
						</Button>
					</Box>
				</Stack>
			</Alert>
		)
	}

	return (
		<Box paddingBottom="4" className="calendar">
			{isFetching ? (
				<Box
					display="flex"
					alignItems="center"
					justifyContent="center"
					position="absolute"
					inset="0"
					pointerEvents="none"
					zIndex="50"
				>
					<Spinner />
				</Box>
			) : null}
			<Box>
				<Box
					as="table"
					width="full"
					maxWidth="full"
					fontSize="sm"
					style={{
						tableLayout: "fixed",
						overflow: "auto",
					}}
					position="relative"
				>
					<thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
						<Box as="tr" textAlign="center">
							{interval.map((day) => {
								const isToday = isSame(now, day, "day")
								const isSelected = isSame(params.date, day, "day")
								return (
									<Box
										as="td"
										key={day.toISOString()}
										borderWidth="1"
										borderColor="transparent"
										bgColor="inset"
									>
										<Box
											display="block"
											width="full"
											paddingY="2"
											boxShadow={{ focus: "outline" }}
											bgColor={{ hover: "subtle" }}
											color={{
												default: getUnit(day, "day") === 0 ? "accent" : "muted",
												hover: getUnit(day, "day") === 0 ? "accent" : "default",
											}}
										>
											<Box textAlign="center">
												<Box display="inlineBlock" fontWeight="semibold">
													{formatDate(day, "ddd")}
												</Box>
											</Box>
											<Box fontSize="xl" fontWeight="semibold">
												<Box
													display="inlineFlex"
													width="10"
													height="10"
													alignItems="center"
													justifyContent="center"
													rounded="full"
													backgroundColor={
														isToday ? "primary_emphasis" : undefined
													}
													color={isToday ? "white" : undefined}
													title={isToday ? "Today" : ""}
												>
													{getUnit(day, "date")}
												</Box>
											</Box>
											<Box fontSize="xs">{formatDate(day, "MMM, YYYY")}</Box>
										</Box>
										{isSelected ? (
											<Box
												height="px"
												bgColor="white"
												position="absolute"
												bottom="0"
												left="0"
												right="0"
											/>
										) : null}
									</Box>
								)
							})}
						</Box>
						{hotel_bookings.length ? (
							<Box as="tr" textAlign="center">
								{dailyStats.map((stat) => (
									<Box
										as="td"
										key={stat.day.toISOString()}
										paddingY="1"
										bgColor="subtle"
										borderWidth="1"
									>
										<Inline
											gap="1"
											alignItems="center"
											justifyContent="center"
											style={{ whiteSpace: "nowrap" }}
										>
											<Stack gap="px" alignItems="center" fontSize={"md"}>
												{stat.checkins || stat.checkouts ? (
													<Inline
														gap="2"
														title={`${stat.checkins} Check-in & ${stat.checkouts} Check-out`}
													>
														<Text fontWeight="semibold">
															<sup style={{ opacity: 0.5, fontSize: ".5em" }}>
																IN
															</sup>{" "}
															{stat.checkins}
														</Text>

														<Text fontWeight="semibold">
															<sup style={{ opacity: 0.5, fontSize: ".5em" }}>
																OUT
															</sup>{" "}
															{stat.checkouts}
														</Text>
													</Inline>
												) : null}
												{stat.total > 0 && (
													<Inline
														gap={sm || md || lg ? undefined : "1"}
														title={`Booked RN: ${stat.booked} • Pending RN: ${stat.pending} • Total RN: ${stat.total}`}
													>
														<Text fontWeight="semibold" color="success">
															{stat.booked}
														</Text>
														+
														<Text fontWeight="semibold" color="danger">
															{stat.pending}
														</Text>
														=<Text fontWeight="semibold">{stat.total}</Text>
													</Inline>
												)}
											</Stack>
										</Inline>
									</Box>
								))}
							</Box>
						) : null}
					</thead>
					<tbody>
						{isFetching && hotel_bookings.length === 0 ? (
							<tr>
								<Box
									as="td"
									colSpan={interval.length}
									bgColor="default"
									borderWidth="1"
									paddingX="4"
									paddingY="12"
									color="muted"
									fontSize="lg"
									textAlign="center"
								/>
							</tr>
						) : hotel_bookings.length === 0 ? (
							<tr>
								<Box
									as="td"
									colSpan={interval.length}
									bgColor="default"
									borderWidth="1"
									padding="4"
									color="muted"
									fontSize="lg"
									textAlign="center"
								>
									No hotel bookings for this interval
								</Box>
							</tr>
						) : (
							<>
								{schedulesPerBookingPerSlot.map((row, i) => (
									<tr key={i}>
										{renderBookingCells(
											row,
											revalidate,
											showResvationsTeam,
											breakpoints
										)}
									</tr>
								))}
							</>
						)}
					</tbody>
				</Box>
			</Box>
		</Box>
	)
}

// Renders a single booking row into table cells while:
// - merging consecutive booking slots using colSpan
// - skipping duplicated booking cells
// - rendering empty date gaps
// - showing continuation indicators for clipped bookings
function renderBookingCells<T extends IBooking>(
	row: Array<TBookingSlot<T>>,
	revalidate: () => void,
	showResvationsTeam: boolean,
	breakpoints: ReturnType<typeof useBreakpoints>
): React.ReactNode[] {
	const cells: React.ReactNode[] = []
	const { sm, md, lg, xl } = breakpoints
	// Iterate through each visible calendar slot
	for (let index = 0; index < row.length; index++) {
		const { date, booking } = row[index]

		// ------------------------------------------------------------------
		// EMPTY SLOT
		// ------------------------------------------------------------------
		// Render an empty table cell when no booking exists for this date.
		if (!booking) {
			cells.push(
				<Box
					as="td"
					key={date.toISOString()}
					borderWidth="1"
					bgColor="default"
				/>
			)

			continue
		}

		// Previous slot booking used to detect repeated booking segments.
		const prevBooking = row[index - 1]?.booking

		// ------------------------------------------------------------------
		// SKIP DUPLICATED BOOKING CELLS
		// ------------------------------------------------------------------
		// Since bookings span across multiple dates, only render the first
		// visible occurrence and skip the remaining repeated slots.
		if (prevBooking?.id === booking.id) {
			continue
		}

		// ------------------------------------------------------------------
		// CALCULATE HORIZONTAL SPAN
		// ------------------------------------------------------------------
		// Count consecutive slots containing the same booking so the booking
		// can be rendered as a single merged <td>.
		let colSpan = 1

		for (let j = index + 1; j < row.length; j++) {
			if (row[j]?.booking?.id === booking.id) {
				colSpan++
			} else {
				break
			}
		}

		// Current calendar viewport boundaries.
		const firstVisibleDate = row[0]?.date
		const lastVisibleDate = row[row.length - 1]?.date

		// Convert booking timestamps into local calendar dates.
		const checkinDate = localOrUtcTimestampToLocalDate(
			booking.checkin_local,
			booking.checkin
		)

		const checkoutDate = localOrUtcTimestampToLocalDate(
			booking.checkout_local,
			booking.checkout
		)

		const startsInView =
			checkinDate >= firstVisibleDate && checkinDate <= lastVisibleDate

		const endsInView =
			checkoutDate >= firstVisibleDate && checkoutDate <= lastVisibleDate

		const can_Edit = Boolean(booking?.can_modify && !booking?.trip?.is_locked)

		// ------------------------------------------------------------------
		// VIEWPORT CLIPPING DETECTION
		// ------------------------------------------------------------------
		// Determines whether the booking started before the current view
		// or continues beyond the visible range.
		const isClippedStart = checkinDate < firstVisibleDate
		const isClippedEnd = checkoutDate > lastVisibleDate

		const stageMeta =
			BOOKING_STAGE_META[booking.booking_stage?.state] ??
			BOOKING_STAGE_META.default

		const { background, borderColor } = stageMeta

		const sidePadding = sm
			? "20px"
			: md
				? "40px"
				: lg
					? "48px"
					: xl
						? "60px"
						: "72px"

		cells.push(
			<Box
				as="td"
				key={`${booking.id}_${date.toISOString()}`}
				colSpan={colSpan}
				borderWidth="1"
				bgColor="default"
				paddingY={"1"}
				verticalAlign="top"
				style={{
					paddingLeft: startsInView ? sidePadding : "1px",
					paddingRight: endsInView ? sidePadding : "1px",
				}}
			>
				<HotelBookingItemInDialog
					hotelBookingId={booking.id}
					readOnly={!can_Edit}
					onChange={revalidate}
				>
					{({ showDetails }) => (
						<Box<"a">
							as="a"
							href="#hotel-booking-details"
							type="button"
							display="block"
							width="full"
							textAlign="left"
							padding="2"
							borderRadius="md"
							borderWidth={"1"}
							borderColor={"danger_emphasis"}
							height={"full"}
							onClick={(e) => {
								e.preventDefault()
								showDetails()
							}}
							style={{
								background,
								borderTop: `1px solid ${borderColor}`,
								borderBottom: `1px solid ${borderColor}`,
								borderLeft: isClippedStart
									? "3px dashed rgba(0,0,0,0.35)"
									: `1px solid ${borderColor}`,
								borderRight: isClippedEnd
									? "3px dashed rgba(0,0,0,0.35)"
									: `1px solid ${borderColor}`,
							}}
						>
							<Tooltip content={<HotelBookingSummary booking={booking} />}>
								<Box>
									<BookingCell
										booking={booking}
										showResvationsTeam={showResvationsTeam}
									/>
								</Box>
							</Tooltip>
						</Box>
					)}
				</HotelBookingItemInDialog>
			</Box>
		)

		// ------------------------------------------------------------------
		// SKIP ALREADY SPANNED SLOTS
		// ------------------------------------------------------------------
		// Move iterator forward to avoid rendering slots already covered
		// by the merged booking cell.
		index += colSpan - 1
	}
	return cells
}

function BookingCell({
	booking,
	showResvationsTeam,
}: {
	booking: IBooking
	showResvationsTeam: boolean
}) {
	const reservationTeam = {
		name: booking?.trip?.reservations_team?.[0]?.name,
		others: booking?.trip?.reservations_team?.length || 0,
	}

	const totalRoomNights =
		booking.room_nights?.reduce((total, room) => {
			const nights = room.nights?.length || 0
			const rooms = room.no_of_rooms || 0

			return total + nights * rooms
		}, 0) || 0

	return (
		<Stack gap="px" minWidth="0" color={"black"}>
			<Text
				fontWeight="semibold"
				textOverflow="truncate"
				whiteSpace="nowrap"
				title={`${booking.hotel?.name}-${booking.hotel.location.name}`}
			>
				{booking.hotel?.name}
			</Text>

			<Box
				fontSize="xs"
				textOverflow="truncate"
				whiteSpace="nowrap"
				overflow="hidden"
				minWidth="0"
				title={`${booking.hotel.location.short_name} • ${totalRoomNights} Room Night`}
			>
				<Inline gap="px" alignItems="center" minWidth="0">
					{joinAttributes(
						booking?.hotel.stars ? (
							<Stars size="3" stars={booking?.hotel.stars} showSingleStar />
						) : null,

						<Text fontSize="xs" textOverflow="truncate" whiteSpace="nowrap">
							{booking.hotel.location.short_name}
						</Text>,

						<Text fontSize="xs" whiteSpace="nowrap" textOverflow="truncate">
							{totalRoomNights} RN
						</Text>,
						<Text fontSize="xs" whiteSpace="nowrap" textOverflow="truncate">
							{booking.no_of_adults + booking.no_of_children} P
						</Text>
					)}
				</Inline>
			</Box>

			{showResvationsTeam && reservationTeam?.name && (
				<Box
					fontSize="xs"
					textOverflow="truncate"
					whiteSpace="nowrap"
					overflow="hidden"
					minWidth="0"
					title={reservationTeam.name}
				>
					<Inline gap="0" alignItems="center" minWidth="0">
						{joinAttributes(
							<Icons.User size="3" />,

							<Text fontSize="xs" textOverflow="truncate" whiteSpace="nowrap">
								{reservationTeam.name}
								{reservationTeam?.others > 1
									? ` +${reservationTeam.others - 1}`
									: ""}
							</Text>
						)}
					</Inline>
				</Box>
			)}
		</Stack>
	)
}

function HotelBookingSummary({ booking }: { booking: IBooking }) {
	const firstDetail = booking?.details?.[0]
	const roomDetails = Object.values(
		(booking.details || []).reduce(
			(acc, detail) => {
				const key = `${detail.room_type?.id}-${detail.meal_plan?.id}`

				if (!acc[key]) {
					acc[key] = {
						roomType: detail.room_type?.display_name,
						mealPlan: detail.meal_plan?.name,
						rooms: 0,
					}
				}

				acc[key].rooms += detail.no_of_rooms || 0

				return acc
			},
			{} as Record<
				string,
				{
					roomType?: string
					mealPlan?: string
					rooms: number
				}
			>
		)
	)

	const totalRooms = roomDetails.reduce((sum, item) => sum + item.rooms, 0)

	const pax = getPaxDisplay(
		booking.no_of_adults,
		booking.no_of_children,
		"full"
	)

	const destination = booking.hotel?.location?.name

	const extraServices = booking.extras || []

	const reservationTeam = booking.trip?.reservations_team
		?.map((u) => u.name)
		.join(", ")

	return (
		<Stack gap="3" fontSize="sm">
			<Stack gap="0">
				<Text>Trip ID: {booking.trip_id}</Text>
				<Text>{booking.supplier?.name || "Direct Hotel"}</Text>
			</Stack>

			<Stack gap="1">
				<Inline gap="2">
					<Icons.Building />
					<Text fontWeight="semibold">{booking.hotel?.name}</Text>
				</Inline>

				<Inline gap="2">
					<Icons.LocationMarker />
					<Text>{destination}</Text>
				</Inline>
			</Stack>

			<Inline gap="2">
				<Icons.Calendar />
				<Inline gap="2">
					<Time value={booking.checkin_local} />{" "}
					<Icons.ArrowLeft rotate="180" />
					<Time value={booking.checkout_local} /> ({booking.no_of_nights} Night
					{booking.no_of_nights > 1 ? "s" : ""})
				</Inline>
			</Inline>

			<Stack gap="1">
				{booking.tourist && (
					<Inline gap="2">
						<Icons.Adult />
						<Text>{booking.tourist.name}</Text>
					</Inline>
				)}

				<Inline gap="2">
					<Icons.Users />
					<Text>{booking.pax_string || pax}</Text>
				</Inline>

				<Inline alignItems="start" gap="2">
					<Icons.Bed />

					<Stack gap="1">
						{roomDetails?.length > 1 && (
							<Text fontWeight="semibold">
								{totalRooms} {totalRooms === 1 ? "Room" : "Rooms"}
							</Text>
						)}
						{roomDetails?.length ? (
							roomDetails.map((room, index) => (
								<Inline key={index}>
									{joinAttributes(
										`${room.rooms} ${room.rooms === 1 ? "Room" : "Rooms"}`,
										room.roomType,
										room.mealPlan
									)}
								</Inline>
							))
						) : (
							<Text>No Room</Text>
						)}
					</Stack>
				</Inline>
			</Stack>

			<Stack gap="2">
				<Inline gap="2">
					{booking.booked && <Icons.OkCircleSolid color="success" />}
					<Text>{booking.booking_stage.title}</Text>
				</Inline>

				{isNumeric(booking?.booked_price) && firstDetail?.currency ? (
					<Inline gap="2">
						<Box>Booking Amount :</Box>

						<Money
							money={moneyParseByDecimal(
								booking.booked_price,
								firstDetail.currency
							)}
							showCurrency
						/>
					</Inline>
				) : null}

				{isNumeric(booking.paid_payments_amount) ? (
					<>
						<Inline gap="2">
							<Box fontWeight="semibold" fontSize="sm">
								Amount Paid:{" "}
							</Box>
							<Money
								amount={booking.paid_payments_amount}
								currency={booking.currency}
								showCurrency
							/>
						</Inline>

						{isNumeric(booking.payments_amount) ? (
							Number(booking.payments_amount || 0) >
							Number(booking.paid_payments_amount || 0) ? (
								<Inline gap="2">
									<Box fontWeight="semibold" fontSize="sm">
										Due Payable:{" "}
									</Box>
									<Money
										amount={
											booking.payments_amount - booking.paid_payments_amount
										}
										currency={booking.currency}
										showCurrency
									/>
								</Inline>
							) : null
						) : null}
					</>
				) : //show paid=0 when "paid_payments_amount" is null && "payments_amount" is not null & "payments_amount" is greater than 0
				isNumeric(booking.payments_amount) &&
				  Number(booking.payments_amount || 0) > 0 ? (
					<Inline gap="2">
						<Box fontWeight="semibold" fontSize="sm">
							Amount Paid:{" "}
						</Box>
						<Money amount={0} currency={booking.currency} showCurrency />
					</Inline>
				) : null}

				{isNumeric(booking.refunding_payments_amount) ? (
					<Inline gap="2">
						<Box fontWeight="semibold" fontSize="sm" gap={"2"}>
							Refund:{" "}
						</Box>
						<Money
							money={moneyParseByDecimal(
								Number(booking.paid_refunding_payments_amount || 0),
								booking.currency
							)}
							showCurrency
						/>
						{" / "}
						<Money
							amount={booking.refunding_payments_amount}
							currency={booking.currency}
							showCurrency
						/>
					</Inline>
				) : null}

				{reservationTeam && (
					<Inline gap="2">
						<Icons.User />
						<Text>{reservationTeam}</Text>
					</Inline>
				)}
			</Stack>

			{extraServices.length > 0 && (
				<Stack gap="1">
					<Text fontWeight="semibold">Extra Services</Text>

					{extraServices.map((extra) => (
						<Inline key={extra.id} gap="2">
							<Icons.Plus />
							<Inline gap={"2"}>
								<Text>
									{extra.service.name} : {"  "}
								</Text>
								<Money
									amount={extra.booked_price}
									currency={extra.currency}
									showCurrency
								/>
							</Inline>
						</Inline>
					))}
				</Stack>
			)}
		</Stack>
	)
}

function NavigationButton({
	onClick,
	days,
}: {
	onClick: (days: number) => void
	days: number
}) {
	const prev = days < 0
	const absDays = Math.abs(days)
	return (
		<Button
			type="button"
			onClick={() => onClick(days)}
			data-testid={prev ? "prev-view" : "next-view"}
		>
			{!prev ? (
				<Box as="sup" color="muted">
					{absDays}
				</Box>
			) : null}
			<Icons.ChevronDown rotate={prev ? "90" : "270"} />
			{prev ? (
				<Box as="sup" color="muted">
					{absDays}
				</Box>
			) : null}
		</Button>
	)
}

function Filters() {
	return (
		<Stack gap="4">
			<DatePickerField
				label={"Select a Date"}
				name={"date"}
				clearable={false}
			/>
			<SelectField
				select={SelectTripDestination}
				name="trip_destinations"
				label="Destinations"
				multiple
				fetchOnMount
			/>
			<SelectField
				label="Trip Sources"
				select={SelectTripSources}
				name="trip_sources"
				multiple
				fetchOnMount
				withRecentlyDisabledWithinDays={30}
			/>
			<SelectField
				select={SelectHotelBookingStages}
				name="booking_stages"
				label="Booking Stages"
				multiple
			/>
			<SelectField
				label="Booking Tags"
				select={SelectHotelBookingStageTags}
				name="booking_tags"
				multiple
				fetchOnMount
			/>
			<SelectField
				label="Payment Status"
				select={SelectHotelBookingPaymentStatuses}
				name="payment_statuses"
				multiple
			/>
			<SelectField
				label="Team"
				select={SelectUsers}
				name="trip_owners"
				multiple
				fetchOnMount
			/>
			<SelectField
				label="Hotels"
				name="hotels"
				select={SelectHotels}
				multiple
			/>
			<SelectField
				label="Hotel Groups"
				name="hotel_groups"
				select={SelectHotelGroup}
				multiple
			/>
			<Stack>
				<label htmlFor="stay_after">Stay During</label>
				<Box display="flex" alignItems="center">
					<DatePickerField name="stay_after" dateFormat="DD MMM, YYYY" />
					<Box>
						<Icons.SwitchHorizontal />
					</Box>
					<DatePickerField
						name="stay_before"
						rightAlign
						dateFormat="DD MMM, YYYY"
					/>
				</Box>
			</Stack>
		</Stack>
	)
}
